import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';

const CARD_VALUE_LABELS = { 1: 'As', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 10: 'Sota', 11: 'Caballo', 12: 'Rey' };
const CARD_SUIT_LABELS = { oros: 'Oros', copas: 'Copas', espadas: 'Espadas', bastos: 'Bastos' };

/** Offline parity (Baciga.jsx's own inline getCardDisplayName, duplicated there for its discard and hide-card pickers) — "Rey de Oros" style, same hardcoded Spanish card names Ronda's own declaration toasts already use online (RondaLogic.getCardValueName). */
function getCardDisplayName(card) {
    if (!card) return '';
    const value = CARD_VALUE_LABELS[card.value] ?? card.value;
    const suit = CARD_SUIT_LABELS[card.suit] ?? card.suit;
    return `${value} de ${suit}`;
}

// TODO.md ("cuando son grises y texto blanco no se leen bien") — a plain
// opacity fade on `:disabled` dims the colored gradient AND the white text
// together, so the contrast between them barely changes even as both wash
// out toward a low-contrast gray-on-gray look. Disabled state now keeps a
// solid, deliberately dark background with clearly legible (if dimmed)
// text instead of just fading the enabled look.
const disabledButtonStyle = {
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.55)',
    boxShadow: 'none',
};

const buttonStyle = {
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '0.95rem',
    padding: '8px 16px',
    borderRadius: '12px',
    minWidth: '110px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
    color: 'white',
    '&:hover': { background: 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)' },
    '&:disabled': disabledButtonStyle,
};

const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    color: '#1a1a1a',
    '&:hover': { background: 'linear-gradient(135deg, #FFE44D 0%, #FFB733 100%)' },
    '&:disabled': disabledButtonStyle,
};

/**
 * Offline parity — fixed bottom-right dark box shared by the discard picker
 * (paso) and the hide-a-card picker (Las31): same positioning/colors as
 * Baciga.jsx:4310-4387 and 4389-4458 respectively, just parameterized here
 * instead of duplicated JSX (offline never extracted a shared piece for
 * these either, but they're visually identical apart from the gold border).
 */
// TODO.md ("Uncaught TypeError: can't access property 'scrollTop', node is
// null") — the SECOND time, on a fresh mount rather than a click. Real root
// cause, distinct from (and underneath) the `in={!disabled}` fix below:
// MUI's <Fade> needs a ref on its immediate child's DOM node (it reads
// `node.scrollTop` on enter to force a reflow) and attaches it via
// `React.cloneElement(children, { ref })` — but a plain function component
// silently DROPS a ref passed to it (React has nowhere to forward it to
// without `forwardRef`), so `Fade`'s internal `nodeRef.current` stayed
// `null` forever for both CardPickerBox-wrapped sections. That's harmless
// while `in` never changes, but the moment either section freshly MOUNTS
// with `in` already true (Fade defaults `appear` to true, so mounting
// already-`in` still plays the enter transition) — exactly what happens
// when a new turn hands this seat `pending.type === 'selectCardToHide'` —
// `reflow(null)` crashes. Forwarding the ref down to the real `Box`/div
// underneath fixes it for both callers, independently of `disabled`.
const CardPickerBox = React.forwardRef(function CardPickerBox({ title, subtitle, goldBorder, bottomOffset, children }, ref) {
    return (
        <Box
            ref={ref}
            sx={{
                position: 'fixed',
                bottom: bottomOffset,
                right: 20,
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: goldBorder ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.7)',
                padding: 2,
                borderRadius: 2,
                maxWidth: '90vw',
                ...(goldBorder && { border: '2px solid #FFD700' }),
            }}
        >
            <Typography
                sx={{
                    color: goldBorder ? '#FFD700' : 'white',
                    fontWeight: goldBorder ? 'bold' : 'normal',
                    fontSize: goldBorder ? '1rem' : '0.9rem',
                    textAlign: 'center',
                }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ color: 'white', fontSize: '0.85rem', textAlign: 'center', mt: -1 }}>{subtitle}</Typography>
            )}
            {children}
        </Box>
    );
});

/**
 * All of online Báciga's decision UI in one presentational component,
 * mirroring RondaDecisionButtons' visual style — unlike Ronda, offline
 * Báciga (Baciga.jsx) never extracted any of this into reusable pieces (see
 * TODO.md's Fase 12c notes), so this is new. `pending` is the whole
 * `GAME_STATE_SYNC.payload.pending` object — the server already privacy-
 * filters it to null unless it's genuinely this seat's turn, so `visible`
 * is simply `Boolean(pending)`. `myHand` drives the card pickers (discards
 * for "paso", the card to hide at the start of Las31).
 */
export default function BacigaDecisionPanel({
    pending,
    myHand = [],
    hasCoveredCard = false,
    disabled = false,
    isMobile = false,
    onTorito,
    onVote,
    onCounterDeclare,
    onSelectCardToHide,
    onHitOrStand,
    onRevealCovered,
    onPreviewDiscard,
}) {
    const { t } = useTranslation();
    const [discardIndices, setDiscardIndices] = useState(new Set());
    // TODO.md ("los botones de la decision... tapan las cartas") — on a
    // narrow screen the fanned hand (OnlineTableRenderer.getPlayerCardPosition,
    // visual slot 0) sits right in the last ~20-40px above the viewport
    // bottom, exactly where this panel used to plant itself. Pushing it up
    // well clear of that band on mobile leaves the hand visible underneath
    // it instead of buried behind the button row.
    const bottomOffset = isMobile ? 130 : 20;
    /** Offline parity (Baciga.jsx's isSelectingCards) — "Paso" no longer votes immediately; it opens this two-step discard picker first (Confirmar/Cancelar), same as offline. */
    const [isSelectingCards, setIsSelectingCards] = useState(false);

    // TODO.md ("aparecen botones marcados de veces anteriores") — pending's
    // own type/seat alone isn't enough to detect a fresh vote: if "todos
    // pasan" cycles within the same hand (bacigaEngine.ts's
    // dealReplacements() re-deals and asks mano to vote again), the SAME
    // seat gets asked `{type:'vote', seat}` again, identical to last time —
    // this effect never re-fired, so whichever discard indices were
    // selected (and their sprites left flipped face-down as a preview) for
    // the OLD hand stayed checked over the brand-new one. myHand's actual
    // contents changing (new cards in) is the real signal a fresh vote
    // deserves a clean slate, so it's included here as a stable content key
    // rather than the array reference itself (which changes every sync
    // regardless of whether the cards did).
    const myHandKey = myHand.map((c) => `${c.suit}${c.value}${c.assignedValue ?? ''}`).join(',');
    useEffect(() => {
        setDiscardIndices(new Set());
        setIsSelectingCards(false);
    }, [pending?.type, pending?.seat, myHandKey]);

    if (!pending) return null;

    // Offline parity (Baciga.jsx's handleToggleCardSelection) — picking a
    // card for discard flips its real on-table sprite face-down immediately,
    // well before "Confirmar Paso" is even pressed; onPreviewDiscard tells
    // OnlineBacigaScreen.jsx which way to flip it (it owns the renderer,
    // this component never touches Pixi directly).
    const toggleDiscard = (idx) => {
        setDiscardIndices((prev) => {
            const next = new Set(prev);
            const willSelect = !next.has(idx);
            if (willSelect) next.add(idx);
            else next.delete(idx);
            onPreviewDiscard?.(idx, willSelect);
            return next;
        });
    };

    const cancelCardSelection = () => {
        discardIndices.forEach((idx) => onPreviewDiscard?.(idx, false));
        setDiscardIndices(new Set());
        setIsSelectingCards(false);
    };

    const voteLabel = (decision) => t(`online.play.baciga.vote_option.${decision}`);

    // Offline parity (Baciga.jsx:4310-4387) — bottom-right box with one text
    // button per hand card, "Confirmar Paso (N)" disabled until 1+ picked, "Cancelar" exits back to the vote buttons without voting.
    if (pending.type === 'vote' && isSelectingCards) {
        return (
            // TODO.md ("Uncaught TypeError: can't access property
            // 'scrollTop', node is null") — this used to be a bare `<Fade
            // in>` (hardcoded true), relying on the PARENT unmounting this
            // whole branch to hide it — safe when that only ever happened
            // on a real pending.type change (paced by the server), but
            // `disabled` now flips the instant an action is sent, well
            // before any such change. Ripping the whole <Fade> out of the
            // tree while react-transition-group's enter lifecycle was still
            // scheduled (it reads the DOM node on the next frame) crashed
            // with the node already gone. `in={!disabled}` instead lets the
            // exit transition run properly, matching the bottom panel's own
            // `in={Boolean(pending)}` pattern below.
            <Fade in={!disabled}>
                <CardPickerBox bottomOffset={bottomOffset} title={t('online.play.baciga.select_discard_prompt')}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {myHand.map((card, idx) => (
                            <Button
                                key={idx}
                                variant={discardIndices.has(idx) ? 'contained' : 'outlined'}
                                color={discardIndices.has(idx) ? 'primary' : 'inherit'}
                                onClick={() => toggleDiscard(idx)}
                                sx={{
                                    minWidth: 100,
                                    color: 'white',
                                    borderColor: 'white',
                                    fontSize: '0.85rem',
                                    textTransform: 'none',
                                    // TODO.md ("sigue mostrándose con poco
                                    // contraste") — theme.js's global MuiButton
                                    // `outlined` override forces a near-opaque
                                    // WHITE background (rgba(255,255,255,0.9))
                                    // on every outlined button app-wide; only
                                    // overriding color/borderColor here left
                                    // white text sitting on that same white
                                    // background. Explicitly overridden for
                                    // both states this button can be in.
                                    ...(discardIndices.has(idx)
                                        ? {}
                                        : { backgroundColor: 'rgba(255, 255, 255, 0.08)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' } }),
                                }}
                            >
                                {getCardDisplayName(card)}
                            </Button>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={disabled || discardIndices.size === 0}
                            onClick={() => {
                                onVote('paso', Array.from(discardIndices));
                                setIsSelectingCards(false);
                            }}
                        >
                            {t('online.play.baciga.confirm_paso_button', { count: discardIndices.size })}
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            sx={{
                                color: 'white',
                                borderColor: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
                            }}
                            onClick={cancelCardSelection}
                        >
                            {t('online.play.baciga.cancel_button')}
                        </Button>
                    </Box>
                </CardPickerBox>
            </Fade>
        );
    }

    // Offline parity (Baciga.jsx:4389-4458) — same box, gold border, single-click per option (no confirm step, matches offline and the interaction online already had here).
    if (pending.type === 'selectCardToHide') {
        return (
            // Same crash-avoiding reasoning as the discard picker above — a
            // single click here calls onSelectCardToHide immediately, which
            // is exactly the case that crashed.
            <Fade in={!disabled}>
                <CardPickerBox
                    goldBorder
                    bottomOffset={bottomOffset}
                    title={t('online.play.baciga.hide_card_title')}
                    subtitle={t('online.play.baciga.hide_card_subtitle')}
                >
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {myHand.map((card, idx) => (
                            <Button
                                key={idx}
                                variant="outlined"
                                disabled={disabled}
                                onClick={() => onSelectCardToHide(idx)}
                                sx={{
                                    minWidth: 100,
                                    color: 'white',
                                    borderColor: '#FFD700',
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    '&:hover': { borderColor: '#FFA500', backgroundColor: 'rgba(255, 215, 0, 0.2)' },
                                    '&:disabled': disabledButtonStyle,
                                }}
                            >
                                {t('online.play.baciga.hide_card_button', { card: getCardDisplayName(card) })}
                            </Button>
                        ))}
                    </Box>
                    <Button variant="contained" color="success" disabled={disabled} onClick={() => onSelectCardToHide(null)} sx={{ mt: 1, fontSize: '0.9rem' }}>
                        {t('online.play.baciga.show_all')}
                    </Button>
                </CardPickerBox>
            </Fade>
        );
    }

    // Everything else keeps the original bottom-center button-row panel.
    let content = null;

    if (pending.type === 'torito') {
        content = (
            <>
                <Typography sx={{ color: 'white', fontSize: '0.85rem', textAlign: 'center', mb: 1 }}>
                    {t('online.play.baciga.torito_prompt')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button sx={primaryButtonStyle} disabled={disabled} onClick={() => onTorito(true)}>
                        {t('online.play.baciga.torito_yes')}
                    </Button>
                    <Button sx={buttonStyle} disabled={disabled} onClick={() => onTorito(false)}>
                        {t('online.play.baciga.torito_no')}
                    </Button>
                </Box>
            </>
        );
    } else if (pending.type === 'vote') {
        content = (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {pending.options.map((decision) => (
                    <Button
                        key={decision}
                        sx={decision === 'baciga' || decision === 'bacigote' ? primaryButtonStyle : buttonStyle}
                        disabled={disabled}
                        onClick={() => (decision === 'paso' ? setIsSelectingCards(true) : onVote(decision, []))}
                    >
                        {voteLabel(decision)}
                    </Button>
                ))}
            </Box>
        );
    } else if (pending.type === 'counterDeclare') {
        content = (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {pending.options.map((decision) => (
                    <Button
                        key={decision}
                        sx={decision === 'baciga' || decision === 'bacigote' ? primaryButtonStyle : buttonStyle}
                        disabled={disabled}
                        onClick={() => onCounterDeclare(decision)}
                    >
                        {voteLabel(decision)}
                    </Button>
                ))}
            </Box>
        );
    } else if (pending.type === 'las31Action') {
        content = (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {pending.options.includes('carta') && (
                    <Button sx={primaryButtonStyle} disabled={disabled} onClick={() => onHitOrStand('carta')}>
                        {t('online.play.baciga.hit')}
                    </Button>
                )}
                {pending.options.includes('planto') && (
                    <Button sx={buttonStyle} disabled={disabled} onClick={() => onHitOrStand('planto')}>
                        {t('online.play.baciga.stand')}
                    </Button>
                )}
                {hasCoveredCard && (
                    <Button sx={buttonStyle} disabled={disabled} onClick={onRevealCovered}>
                        {t('online.play.baciga.reveal_covered')}
                    </Button>
                )}
            </Box>
        );
    }
    // 'bindWildcard' is handled separately by WildcardSelector's own dialog, not here.

    if (!content) return null;

    // TODO.md ("el modal... debería desaparecer una vez se pulse un
    // botón") — `disabled` (OnlineBacigaScreen.jsx's processingDecision)
    // turns true the instant an action is SENT, before the server's reply
    // confirms it. Folded into this already-existing `in` condition
    // (instead of unmounting the whole component higher up, which crashed
    // the two CardPickerBox branches above — see their own doc comments)
    // so the panel fades out right away rather than sitting there,
    // disabled, until the next sync catches up.
    return (
        <Fade in={Boolean(pending) && !disabled}>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: bottomOffset,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    background: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                    maxWidth: '95vw',
                }}
            >
                {content}
            </Box>
        </Fade>
    );
}
